// App root: store status (login / loading / error) → AppShell + the routed view (DESIGN §2–§3).
import './app.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useStore, reload, login, lastLoginId, MODE } from './store.js';
import { useRoute, href, navigate } from './router.js';
import { Button, EmptyState, Field, TextInput, ToastHost } from './components/ui.jsx';
import { AppShell } from './components/app-shell.jsx';
import { Page } from './components/page.jsx';
import { Wordmark } from './components/sidebar.jsx';
import { OverviewView } from './views/overview.jsx';
import { WorkspaceView } from './views/workspace.jsx';
import { ClientsView } from './views/clients.jsx';
import { ClientView } from './views/client.jsx';
import { BatchView } from './views/batch.jsx';
import { CalendarView } from './views/calendar.jsx';
import { PerformanceView } from './views/performance.jsx';
import { PeopleView, PersonView } from './views/people.jsx';
import { SettingsView } from './views/settings.jsx';

function NotFound() {
  return (
    <Page title="Page not found">
      <EmptyState
        title="This page does not exist or has moved."
        action={<Button href={href('/')}>Go to Overview</Button>}
      />
    </Page>
  );
}

function RouteView({ route }) {
  switch (route.name) {
    case 'overview': return <OverviewView route={route} />;
    case 'workspace': return <WorkspaceView route={route} />;
    case 'clients': return <ClientsView route={route} />;
    case 'client': return <ClientView key={route.params.clientId} route={route} />;
    case 'batch': return <BatchView key={route.params.batchId} route={route} />;
    case 'calendar': return <CalendarView route={route} />;
    case 'performance': return <PerformanceView route={route} />;
    case 'people': return <PeopleView route={route} />;
    case 'person': return <PersonView key={route.params.memberId} route={route} />;
    case 'settings': return <SettingsView route={route} />;
    default: return <NotFound />;
  }
}

// Quiet: shows nothing for the first 400 ms so fast loads never flash a message.
function Loading() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(timer);
  }, []);
  return (
    <div class="app-status" role="status" aria-live="polite">
      {visible && <span class="meta">Loading workspace…</span>}
    </div>
  );
}

function LoadError({ message }) {
  const [busy, setBusy] = useState(false);
  const retry = () => {
    setBusy(true);
    reload().finally(() => setBusy(false));
  };
  return (
    <div class="app-status" role="alert">
      <p class="app-status-title">Could not load the workspace.</p>
      {message && <p class="meta">{message}</p>}
      <Button variant="secondary" onClick={retry} disabled={busy}>Retry</Button>
    </div>
  );
}

// Personal sign-in (DESIGN §8): name or login ID + password. Enter submits; the browser can fill both.
// The error shows under the field it is about; `notice` says why a session ended (password reset, account off).
function LoginScreen({ notice }) {
  const [loginId, setLoginId] = useState(() => lastLoginId());
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorField, setErrorField] = useState('password');
  const [busy, setBusy] = useState(false);
  const idRef = useRef(null);
  const pwRef = useRef(null);
  useEffect(() => { (loginId ? pwRef : idRef).current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!loginId.trim()) {
      setErrorField('id');
      setError('Enter your name or login ID.');
      idRef.current?.focus();
      return;
    }
    if (!password) {
      setError('Enter your password.');
      pwRef.current?.focus();
      return;
    }
    setErrorField('password');
    setBusy(true);
    setError('');
    try {
      const user = await login(loginId, password);
      // Still on a password someone else set (SEC-06): go straight to Settings → Change password.
      if (user?.temporaryPassword) navigate('/settings', { replace: true });
    } catch (err) {
      setError(err.message || 'Incorrect name or password.');
      setBusy(false);
      pwRef.current?.select();
    }
  };
  const edit = (set) => (v) => { set(v); if (error) setError(''); };

  return (
    <main class="login">
      <form class="login-card" onSubmit={submit} noValidate>
        <div class="login-brand">
          <Wordmark height={40} />
          <h1 class="login-title sr-only">Sign in to FRAZIER</h1>
        </div>
        {notice && !error && <p class="login-notice" role="status">{notice}</p>}
        <Field label="Name or login ID" error={errorField === 'id' ? error : ''}>
          <TextInput inputRef={idRef} name="username" autoComplete="username" autoCapitalize="none" spellCheck={false}
            value={loginId} onValue={edit(setLoginId)} invalid={!!error && errorField === 'id'} />
        </Field>
        <Field label="Password" error={errorField === 'password' ? error : ''}>
          <TextInput inputRef={pwRef} type="password" name="password" autoComplete="current-password"
            value={password} onValue={edit(setPassword)} invalid={!!error && errorField === 'password'} />
        </Field>
        <Button type="submit" variant="primary" size="lg" block disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</Button>
      </form>
    </main>
  );
}

export function App() {
  const { status, state, error, notice } = useStore();
  const route = useRoute();

  if (status === 'unauthenticated' && MODE === 'server') {
    return (
      <>
        <LoginScreen notice={notice} />
        <ToastHost />
      </>
    );
  }

  if (!state) {
    return (
      <>
        {status === 'error' ? <LoadError message={error} /> : <Loading />}
        <ToastHost />
      </>
    );
  }

  return (
    <AppShell route={route}>
      <RouteView route={route} />
    </AppShell>
  );
}
